// Auto-generated by scripts/generate-latest-updates.js. Do not edit.

export type LatestUpdateCategory = "Coverage" | "Data quality" | "Discovery" | "Maps" | "Platform" | "Search";

export interface LatestUpdate {
  id: string;
  date: string;
  title: string;
  summary: string;
  category: LatestUpdateCategory;
  highlights?: string[];
  link?: string;
}

export const latestUpdatesLastModified = "2026-09-06";

export const latestUpdates: LatestUpdate[] = [
  {
    "id": "2026-09-06-university-event-coverage-and-campus-aware-locations",
    "date": "2026-09-06",
    "title": "University event coverage and campus-aware locations",
    "summary": "Event Finder now supports a pilot group of large universities, with clearer hosted-event details and campus-aware map locations when a specific venue is unavailable.",
    "category": "Coverage",
    "link": "/browse"
  },
  {
    "id": "2026-09-02-flexible-upcoming-event-ranges",
    "date": "2026-09-02",
    "title": "Choose how far ahead to explore",
    "summary": "Event lists now open with the next 30 days and make it easy to widen discovery to the next 90 or 180 days.",
    "category": "Discovery",
    "highlights": [
      "Browse, entity and location pages, My Events, Event Groups, search results, and Events Near Me share clear 30-, 90-, and 180-day options.",
      "The 30-day range is the default, while wider selections stay in the page URL so filtered views can be refreshed or shared.",
      "Lists, maps, result totals, facets, and complete exports use the same selected range.",
      "On event pages, Events Near Me now sits beside Back for quicker navigation and uses the 30-day nearby count.",
      "The header's Create Event Groups action now uses a clearer, unshaded collection icon."
    ],
    "link": "/browse"
  },
  {
    "id": "2026-09-02-remember-events-and-explore-nearby",
    "date": "2026-09-02",
    "title": "Remember events and explore what is nearby",
    "summary": "Save events in this browser, revisit them in a complete My Events workspace, and use the expanded Events Near Me experience to find relevant plans around you.",
    "category": "Discovery",
    "highlights": [
      "Use Remember Event on event cards, list rows, map details, related events, or an event page without creating an account.",
      "My Events separates Upcoming from the previous seven days, shares filters across the list and map, and exports every filtered result as CSV, Markdown, or text.",
      "Remove one event or clear the Past section at once; browser collections are capped at 1,000 saves and expire after 12 months without a visit.",
      "Events Near Me now uses kilometer radii, a 15 km and 21-day default, complete exports, responsive map details, and a nearby event count on event pages when location is already allowed.",
      "The home page now shows up to 18 entity-ranked Popular Events and 18 Top Event Cities, with stable ordering when counts tie.",
      "Navigation now promotes My Events, Events Near Me, and icon-only Event Groups while keeping the full Categories page available by direct link."
    ],
    "link": "/my-events"
  },
  {
    "id": "2026-08-29-related-events-on-event-pages",
    "date": "2026-08-29",
    "title": "See related events from any event page",
    "summary": "Every event page now points to other events worth knowing about: what else is happening around the same time, and what else covers similar ground.",
    "category": "Discovery",
    "highlights": [
      "For in-person events, see what else is happening within 20 miles on the day before, the day of, or the day after.",
      "For online events, see other online sessions the same day, labelled as starting before, after, or at the same time.",
      "Similar events surfaces upcoming events that share a format and a subject, whether that is a hands-on workshop, a keynote, or a networking evening.",
      "Multi-day conferences running through the date are marked as ongoing rather than buried by their original start date.",
      "Each suggestion stays compact: the name, the date and time where it is known, and the venue or city."
    ],
    "link": "/browse"
  },
  {
    "id": "2026-08-22-build-and-share-event-groups",
    "date": "2026-08-22",
    "title": "Build and share Event Groups",
    "summary": "Create a private-by-discovery page that brings events from selected Tech Companies and Corporations into one shareable view, with the same rich exploration tools as organization pages.",
    "category": "Discovery",
    "highlights": [
      "Search for and select 2 to 50 Tech Companies and Corporations; submitting the same set again reuses its existing group rather than creating duplicates.",
      "Every group gets a memorable adjective-and-mythical-creature link that can be copied or shared directly, with no account or sign-in required.",
      "Use date, format, relationship, time-of-day, vibe, and Entity filters to focus the list while keeping filtered URLs shareable.",
      "Explore every matching mapped location and export the complete filtered result as CSV, Markdown, or plain text, even when the on-page list is paginated.",
      "Group pages are unlisted and excluded from search engines; anonymous creation includes abuse controls, an inline privacy notice, and operational telemetry without retaining raw IP addresses."
    ],
    "link": "/groups/new"
  },
  {
    "id": "2026-08-18-a-clearer-guide-and-public-update-history",
    "date": "2026-08-18",
    "title": "A clearer guide and public update history",
    "summary": "A simpler guide now explains how Event Finder discovers and checks events, and a new page shares recent improvements.",
    "category": "Platform",
    "link": "/how-it-works"
  },
  {
    "id": "2026-08-17-better-source-matching",
    "date": "2026-08-17",
    "title": "Better matching between organizations and event websites",
    "summary": "Event Finder now checks more evidence before connecting an organization to an event website, helping reduce unrelated sources while keeping useful listings available.",
    "category": "Discovery",
    "link": "/browse"
  },
  {
    "id": "2026-08-17-cleaner-event-lists",
    "date": "2026-08-17",
    "title": "Cleaner event lists and dependable filters",
    "summary": "Event lists now remove repeated records more consistently, and discovery filters stay connected to the selected organizations and locations.",
    "category": "Search",
    "link": "/browse"
  },
  {
    "id": "2026-08-17-live-coverage-metrics",
    "date": "2026-08-17",
    "title": "Live event coverage on the home page",
    "summary": "The home page now shows current totals for upcoming events, event formats, organizations, websites, cities, and countries, with a clear refresh time.",
    "category": "Platform",
    "link": "/"
  },
  {
    "id": "2026-08-16-global-cities-and-shareable-links",
    "date": "2026-08-16",
    "title": "Global city coverage and cleaner links",
    "summary": "You can browse events across a much broader worldwide city list and share readable links for cities, organizations, and individual events.",
    "category": "Coverage",
    "link": "/map"
  },
  {
    "id": "2026-08-16-resilient-event-reading",
    "date": "2026-08-16",
    "title": "More resilient event reading",
    "summary": "Event Finder can choose the most suitable reading method for normal page text and visual page layouts, improving coverage when one method is not enough.",
    "category": "Discovery",
    "link": "/how-it-works"
  },
  {
    "id": "2026-08-15-faster-lists-accurate-maps",
    "date": "2026-08-15",
    "title": "Faster event lists and more accurate map pins",
    "summary": "Organization event pages load much faster, and map pins use venue coordinates when they are available instead of defaulting to the center of a city.",
    "category": "Maps",
    "link": "/map"
  },
  {
    "id": "2026-08-14-source-local-event-times",
    "date": "2026-08-14",
    "title": "Event times that match the source website",
    "summary": "Event dates and times now preserve the local wall-clock values published by the event website, avoiding accidental shifts during processing.",
    "category": "Data quality",
    "link": "/browse"
  },
  {
    "id": "2026-05-19-location-venue-foundation",
    "date": "2026-05-19",
    "title": "A stronger foundation for venue and location details",
    "summary": "Structured venue and location records created the foundation for better geographic search, event maps, and more useful place information.",
    "category": "Data quality",
    "link": "/map"
  }
];
